import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	EMPLOYMENT_TYPE_OPTIONS,
	type EmploymentType,
} from "@/lib/therapist-scheduling/constants";
import { cn } from "@/lib/utils";

type EmploymentTypeFilterProps = {
	value: EmploymentType;
	onChange: (value: EmploymentType) => void;
	disabled?: boolean;
	className?: string;
};

/**
 * Step 1 of the schedule flow (filter → date → slot). Segmented KARPIS / MITRA
 * filter that sits above the calendar. The selected value maps to the external
 * `therapist_type` param upstream (KARPIS → internal, FLAT → external).
 */
export function EmploymentTypeFilter({
	value,
	onChange,
	disabled = false,
	className,
}: EmploymentTypeFilterProps) {
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.schedule_picker.employment_filter",
	});
	return (
		<div
			className={cn(
				"flex flex-col gap-3 rounded-md border border-border bg-sidebar p-3 md:flex-row md:items-center md:justify-between",
				className,
			)}
		>
			<div>
				<p className="font-semibold text-sm">{t("title")}</p>
				<p className="text-pretty text-muted-foreground text-xs">
					{t("description")}
				</p>
			</div>
			<div className="flex gap-2">
				{EMPLOYMENT_TYPE_OPTIONS.map((option) => (
					<Button
						key={option.value}
						type="button"
						size="sm"
						variant={value === option.value ? "default" : "outline"}
						aria-pressed={value === option.value}
						disabled={disabled}
						onClick={() => onChange(option.value)}
					>
						{option.label}
					</Button>
				))}
			</div>
		</div>
	);
}
